import { createRouter, createWebHistory } from "vue-router";
import { storeToRefs } from "pinia";

import { useGlobalStore, useUserStore } from "@/store";
import { userHasRole } from "@/utils/roles";

/** Layouts */
import MainLayout from "../layouts/MainLayout.vue";
import AuthLayoutVue from "@/layouts/AuthLayout.vue";

/** Views */
import LogoutView from "@/views/LogoutView.vue";
import RoleSelectView from "@/views/RoleSelectView.vue";

import DashboardView from "@/views/DashboardView.vue";
import WebsiteData from "@/views/WebsiteData.vue";

import ErrorPage from "@/views/ErrorPage.vue";
import AcceptTerms from "@/views/orders/AcceptTerms.vue";

import SurveyIndex from "@/views/feedback/SurveyIndex.vue";
import SurveyPage from "@/views/feedback/SurveyPage.vue";
import SurveyResponse from "@/views/feedback/SurveyResponse.vue";

import SatifactionSurveyUserList from "@/views/feedback/SatifactionSurveyUserList.vue";
import SatifactionSurveyClub from "@/views/feedback/SatifcationSurveyClub.vue";

import MemberOutcomesIndex from "@/views/member_outcomes/MemberOutcomesIndex.vue";
import MemberOutcomePage from "@/views/member_outcomes/MemberOutcomePage.vue";

// TODO: Sort this out
import ClubFeedbackPage from "@/views/surveys/SurveyIndex.vue";

import { resourceRoutes } from "./resources";
import { discountRoutes } from "./discounts";
import { profileRoutes } from "./profile";
import { courseRoutes } from "./courses";
import { parentRoutes } from "./parents";
import { orderRoutes } from "./orders";
import { clubRoutes } from "./clubs";
import { userRoutes } from "./users";
import { authRoutes } from "./auth";

const routes = [
  {
    path: "/",
    component: AuthLayoutVue,
    children: authRoutes,
  },
  {
    path: "/dashboard",
    name: "DashboardLayout",
    component: MainLayout,
    children: [
      ...discountRoutes,
      ...resourceRoutes,
      ...profileRoutes,
      ...parentRoutes,
      ...courseRoutes,
      ...orderRoutes,
      ...clubRoutes,
      ...userRoutes,
      {
        path: "/dashboard/",
        name: "Dashboard",

        component: DashboardView,
        meta: {
          name: "Dashboard",
          breadcrumbs: ["Dashboard"],
        },
      },
      {
        path: "/dashboard/onboarding",
        name: "Onboarding",
        component: RoleSelectView,
        meta: {
          name: "Select Account Type",
          breadcrumbs: ["Dashboard", "Onboarding"],
        },
      },
      {
        path: "/dashboard/websitedata/",
        name: "Website Data",
        component: WebsiteData,
        meta: {
          name: "Website Data",
          breadcrumbs: ["Dashboard", "Website Data"],
        },
      },
      {
        path: "/dashboard/actions/:id",
        name: "Course_Prerequisites",

        component: AcceptTerms,
        meta: {
          name: "Course Prerequisites",
          breadcrumbs: ["Dashboard", "Course_Prerequisites"],
        },
      },

      {
        path: "/dashboard/training-feedback",
        name: "Training_Feedback",
        component: SurveyIndex,
        meta: {
          name: "Training Feedback",
          breadcrumbs: ["Dashboard", "Training_Feedback"],
        },
      },

      {
        path: "/dashboard/training-feedback/:id",
        name: "Survey",
        component: SurveyPage,
        meta: {
          name: "Survey",
          breadcrumbs: ["Dashboard", "Training_Feedback", "Survey"],
        },
      },

      {
        path: "/dashboard/training-feedback/:id/reponse/:user_id",
        name: "Survey_Response",
        component: SurveyResponse,
        meta: {
          name: "Survey Response",
          breadcrumbs: ["Dashboard", "Training_Feedback", "Survey", "Survey_Response"],
        },
      },

      {
        path: `/dashboard/satisfaction-survey`,
        name: "Satisfaction_Survey",
        component: SatifactionSurveyUserList,
        meta: {
          name: "Satisfaction Survey",
          breadcrumbs: ["Dashboard", "Satisfaction_Survey"],
        },
      },

      {
        path: `/dashboard/satisfaction-survey-club`,
        name: "Satisfaction_Survey_Club",
        component: SatifactionSurveyClub,
        meta: {
          name: "Satisfaction Survey",
          breadcrumbs: ["Dashboard", "Satisfaction_Survey_Club"],
        },
      },

      {
        path: `/dashboard/club-member-outcomes`,
        name: "Member_Outcomes",
        component: MemberOutcomesIndex,
        meta: {
          name: "Member Outcomes",
          breadcrumbs: ["Dashboard", "Member_Outcomes"],
        },
      },

      {
        path: "/dashboard/club-member-outcomes/:member_id",
        name: "Member_Outcome",
        component: MemberOutcomePage,
        meta: {
          name: "Member Outcome",
          breadcrumbs: ["Dashboard", "Member_Outcomes", "Member_Outcome"],
        },
      },
      {
        path: "/dashboard/member-outcomes",
        name: "Member_Outcomes_Rename",
        component: SurveyIndex,
        meta: {
          name: "Member Outcomes",
          breadcrumbs: ["Dashboard", "Member_Outcomes"],
        },
      },
      {
        path: "/dashboard/member-outcomes/:id",
        name: "Club_Feedback",
        component: ClubFeedbackPage,
        meta: {
          name: "Club Feedback",
          breadcrumbs: ["Dashboard", "Member_Outcomes", "Club_Feedback"],
        },
      },
      { path: "/error", name: "Error", component: ErrorPage, meta: { name: "Error" } },
      { path: "/:catchAll(.*)", redirect: { name: "Error" } },
    ],
  },
  {
    path: "/logout",
    name: "Logout",
    component: LogoutView,
    meta: {
      name: "Logout",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async to => {
  document.title = `${to.meta.name ?? "Portal"} | Play Included`;

  const { isUserLoading, isUserOnboarded, user } = useUserStore();
  const { isMobileMenuOpen } = storeToRefs(useGlobalStore());

  if (isMobileMenuOpen) {
    isMobileMenuOpen.value = false;
  }

  const allowedRoutes = ["/login", "/signup", "/logout", "/dashboard/onboarding"];

  try {
    // If user is an admin, let them through
    if (userHasRole(user.roles, ["admin", "clubs-admin", "courses-admin", "users-admin"])) {
      return;
    }

    /** Redirect to Onboarding if user is not onboarded */
    if (!isUserLoading && !isUserOnboarded && !allowedRoutes.includes(to.fullPath)) {
      return { name: "Onboarding" };
    }

    /** Prevent onboarded users from returning to Onboarding screen */
    if (!isUserLoading && to.name === "Onboarding" && isUserOnboarded) {
      return { name: "Dashboard" };
    }

    const allowedPaths = [
      "/dashboard",
      "/dashboard/profile",
      "/dashboard/profile/subscription",
      "/dashboard/profile/security",
      "/dashboard/profile/email-preferences",
      "/logout",
      "/error",
    ];

    const subscriptionStatuses = ["expired", "pending", "cancelled"];

    /** Prevent expired subbed users from navigating anywhere */
    if (subscriptionStatuses.includes(user.subscription_status) && to.path !== "/logout" && !allowedPaths.includes(to.path)) {
      return { name: "Dashboard" };
    }

    /** Prevent access to Brick Clubs if they're not enabled */
    if (to.path.includes("/clubs") && process.env.VUE_APP_BRICK_CLUBS_ENABLED === false) {
      return { name: "Dashboard" };
    }

    /** Prevent lower roles from accessing events list */
    // Redirect to courses page if user is not an admin or trainer
    if (
      userHasRole(user.roles, ["parent", "brick-club-family", "professional", "certified-facilitator-trainee", "certified-facilitator", "advanced-facilitator-trainee", "advanced-facilitator"]) &&
      to.name === "Training_Events"
    ) {
      return { name: "Courses" };
    }
  } catch (err) {
    // Do nothing
  }
});

export default router;
